import type { SsoParams, SsoResponse, SsoType } from './types';

/**
 * SSO types that map to well-known API endpoints rather than the generic
 * `/api/sso/{type}` pattern.
 */
const SSO_ENDPOINTS: Record<string, string> = {
  weblinksaml: '/api/sso/weblink',
  wa: '/api/sso/wealthaccess',
};

/** SSO types whose credentials must be sent as a POST body. */
const POST_SSO_TYPES = new Set(['clientpoint', 'saml', 'household', 'ncr']);

function normalizeSsoTypeInput(ssoType: SsoType): string {
  return String(ssoType)
    .replace(/^\uFEFF/, '')
    .trim()
    .toLowerCase();
}

function trimSsoTypeRaw(ssoType: SsoType): string {
  return String(ssoType)
    .replace(/^\uFEFF/, '')
    .trim();
}

function isBairdSsoFamily(normalizedType: string): boolean {
  return normalizedType === 'baird' || normalizedType === '_baird';
}

function isSalesforceSsoFamily(normalizedType: string): boolean {
  return normalizedType === 'salesforce' || normalizedType === '_salesforce';
}

/**
 * Baird route on the platform. Default `/api/sso/Baird` (PascalCase).
 * Override with `VITE_BAIRD_SSO_PATH` at widget build if your host differs.
 */
function getBairdSsoPath(): string {
  const raw = import.meta.env.VITE_BAIRD_SSO_PATH;
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (t) {
      return t.startsWith('/') ? t : `/${t}`;
    }
  }
  return '/api/sso/Baird';
}

/**
 * Resolve the SSO path. Baird maps to `getBairdSsoPath()`; unknown types keep the
 * caller's segment casing (e.g. `ncrr` → `/api/sso/ncrr`, `Baird` → `/api/sso/Baird`
 * via the Baird branch).
 */
function resolveSsoEndpointPath(
  ssoTypeRaw: string,
  normalizedType: string,
): string {
  if (isBairdSsoFamily(normalizedType)) {
    return getBairdSsoPath();
  }
  if (SSO_ENDPOINTS[normalizedType]) {
    return SSO_ENDPOINTS[normalizedType];
  }
  const segment = ssoTypeRaw || normalizedType;
  return `/api/sso/${segment}`;
}

/** All SSO traffic uses the platform host; `servicesUrl` is for API data only. */
function resolveSsoBaseUrl(platformUrl?: string): string {
  const trimmed = platformUrl?.trim();
  return trimmed || getDefaultPlatformUrl();
}

/** Join a base URL that may include a path prefix with an app-root path (e.g. `/api/sso/foo`). */
function resolveUrlAgainstBase(baseUrl: string, path: string): URL {
  const base = new URL(baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`);
  const relativePath = path.startsWith('/') ? path.slice(1) : path;
  return new URL(relativePath, base);
}

function buildSsoGetHeaders(
  normalizedType: string,
  role: string,
): Record<string, string> {
  if (isBairdSsoFamily(normalizedType)) {
    return {};
  }
  return { currentRole: role };
}

function setIsInvestorQueryParam(role: string, url: URL): void {
  const isInvestor = role.toLowerCase() === 'investor';
  url.searchParams.set('isInvestor', isInvestor ? 'true' : 'false');
}

function toSsoQueryParamValue(value: unknown): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || null;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return null;
}

/**
 * Apply GET query params per SSO family. Empty strings are omitted.
 */
function applyGetSsoQueryParams(
  normalizedType: string,
  role: string,
  credentials: SsoParams,
  url: URL,
): void {
  const token =
    typeof credentials.token === 'string' ? credentials.token.trim() : '';
  if (token) {
    url.searchParams.set('token', token);
  }

  // Baird: platform accepts SAML in `token` + `isInvestor` only (no investorEmail).
  if (isBairdSsoFamily(normalizedType)) {
    setIsInvestorQueryParam(role, url);
    return;
  }

  if (isSalesforceSsoFamily(normalizedType)) {
    const advisor =
      typeof credentials.advisor === 'string' ? credentials.advisor.trim() : '';
    const investor =
      typeof credentials.investor === 'string'
        ? credentials.investor.trim()
        : '';
    if (advisor) {
      url.searchParams.set('advisor', advisor);
    }
    if (investor) {
      url.searchParams.set('investor', investor);
    }
    return;
  }

  for (const [key, value] of Object.entries(credentials)) {
    if (key === 'token') {
      continue;
    }
    if (value !== undefined) {
      const scalar = toSsoQueryParamValue(value);
      if (scalar) {
        url.searchParams.set(key, scalar);
      }
    }
  }
}

function filterSsoPostBody(credentials: SsoParams): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(credentials)) {
    if (value === undefined || value === null) {
      continue;
    }
    if (typeof value === 'string' && value.trim() === '') {
      continue;
    }
    body[key] = value;
  }
  return body;
}

export function getDefaultServicesUrl(): string {
  return import.meta.env.VITE_SERVICES_URL as string;
}

export function getDefaultPlatformUrl(): string {
  return import.meta.env.VITE_PLATFORM_URL as string;
}

/**
 * Authenticate via an SSO endpoint on the **platform** base URL only.
 * `servicesUrl` is not used for SSO; it is for post-auth API / data calls.
 */
export async function ssoAuthenticate(
  role: string,
  ssoType: SsoType,
  credentials: SsoParams,
  servicesUrl: string,
  platformUrl?: string,
): Promise<SsoResponse> {
  void servicesUrl;

  const ssoTypeRaw = trimSsoTypeRaw(ssoType);
  const normalizedType = normalizeSsoTypeInput(ssoType);
  const endpoint = resolveSsoEndpointPath(ssoTypeRaw, normalizedType);
  const isPost = POST_SSO_TYPES.has(normalizedType);

  const baseUrl = resolveSsoBaseUrl(platformUrl);
  const url = resolveUrlAgainstBase(baseUrl, endpoint);

  let response: Response;

  if (isPost) {
    response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        currentRole: role,
      },
      body: JSON.stringify(filterSsoPostBody(credentials)),
    });
  } else {
    applyGetSsoQueryParams(normalizedType, role, credentials, url);

    response = await fetch(url, {
      method: 'GET',
      headers: buildSsoGetHeaders(normalizedType, role),
    });
  }

  if (!response.ok) {
    let detail = '';
    try {
      const bodyText = await response.text();
      if (bodyText) {
        detail =
          bodyText.length > 280 ? `${bodyText.slice(0, 280)}…` : bodyText;
      }
    } catch {
      // ignore body read failures
    }
    const statusPart =
      `${String(response.status)} ${response.statusText}`.trim();
    throw new Error(
      detail
        ? `SSO authentication failed (${statusPart}): ${detail}`
        : `SSO authentication failed: ${statusPart}`,
    );
  }

  return (await response.json()) as SsoResponse;
}
